import { useParams } from 'react-router-dom'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, Check, CheckCheck, CheckCircle2, Clock, Loader2, Repeat, RotateCcw, Search, Send, UserPlus,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { getMembros, type MembroComPerfil } from '@/services/equipe'
import { listPacientes } from '@/services/pacientes'
import { converterEmTarefa, listModelos, type Mensagem, type MensagemModelo } from '@/services/mensagens'
import {
  atenderConversa, encerrarConversa, enviarWhatsApp, formatarTelefone, getCanal, getConversa, horasRestantes,
  janelaAberta, liberarConversa, listTemplates, marcarLida, nomeConversa,
  preencherTemplate, reabrirConversa, repassarConversa, vincularPaciente,
  type CanalConexao, type Conversa, type WhatsTemplate,
} from '@/services/atendimento'
import { PAPEL_LABEL } from '@/lib/types'

export default function ConversaPage() {
  const { id } = useParams<{ id: string }>()
  const { current, user } = useAuth()
  const [conversa, setConversa] = useState<Conversa | null>(null)
  const [mensagens, setMensagens] = useState<Mensagem[]>([])
  const [membros, setMembros] = useState<MembroComPerfil[]>([])
  const [modelos, setModelos] = useState<MensagemModelo[]>([])
  const [templates, setTemplates] = useState<WhatsTemplate[]>([])
  const [canal, setCanal] = useState<CanalConexao | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [texto, setTexto] = useState('')
  const [repassando, setRepassando] = useState(false)
  const [repasseAlvo, setRepasseAlvo] = useState('')

  const [tplId, setTplId] = useState('')
  const [tplVars, setTplVars] = useState<string[]>([])

  const [vinculando, setVinculando] = useState(false)
  const [buscaPac, setBuscaPac] = useState('')
  const [sugestoes, setSugestoes] = useState<{ id: string; nome: string }[]>([])

  const fimRef = useRef<HTMLDivElement>(null)

  const carregar = useCallback(async (comLoading = false) => {
    if (!current || !id) return
    if (comLoading) setLoading(true)
    try {
      const r = await getConversa(current.workspace.id, id)
      if (r) { setConversa(r.conversa); setMensagens(r.mensagens) }
      const [ms, mods, tpls, ch] = await Promise.all([
        getMembros(current.workspace.id),
        listModelos(current.workspace.id),
        listTemplates(current.workspace.id),
        getCanal(current.workspace.id),
      ])
      setMembros(ms.filter(m => m.ativo && m.user_id))
      setModelos(mods)
      setTemplates(tpls.filter(t => t.status === 'APPROVED'))
      setCanal(ch)
    } finally { setLoading(false) }
  }, [current, id])

  useEffect(() => { carregar(true) }, [carregar])
  useEffect(() => {
    if (conversa && conversa.nao_lidas > 0) marcarLida(conversa.id).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversa?.id])
  useEffect(() => {
    const t = setInterval(() => carregar(), 12000)
    return () => clearInterval(t)
  }, [carregar])
  useEffect(() => { fimRef.current?.scrollIntoView({ block: 'end' }) }, [mensagens.length])
  useEffect(() => {
    if (!current || buscaPac.trim().length < 2) { setSugestoes([]); return }
    const t = setTimeout(async () => {
      const ps = await listPacientes(current.workspace.id, { busca: buscaPac, situacao: 'todas' })
      setSugestoes(ps.slice(0, 8).map(p => ({ id: p.id, nome: p.nome })))
    }, 250)
    return () => clearTimeout(t)
  }, [current, buscaPac])

  const nomeMembro = useCallback(
    (uid: string | null) => membros.find(m => m.user_id === uid)?.perfil?.nome ?? 'Equipe',
    [membros]
  )

  async function assumir() {
    if (!conversa) return
    setBusy(true); setErro(null)
    try {
      const r = await atenderConversa(conversa.id)
      if (!r.ok) setErro(`Já está sendo atendida por ${nomeMembro(r.atendente_user_id)}.`)
      await carregar()
    } finally { setBusy(false) }
  }
  async function liberar() {
    if (!conversa) return
    setBusy(true)
    try { await liberarConversa(conversa.id); await carregar() } finally { setBusy(false) }
  }
  async function repassar() {
    if (!conversa || !repasseAlvo) return
    setBusy(true); setErro(null)
    try {
      const r = await repassarConversa(conversa.id, repasseAlvo)
      if (!r.ok) { setErro('Não foi possível repassar a conversa.'); return }
      setRepassando(false); setRepasseAlvo('')
      await carregar()
    } finally { setBusy(false) }
  }
  async function finalizar() {
    if (!conversa || !current) return
    if (!window.confirm('Finalizar esta conversa?')) return
    setBusy(true)
    try { await encerrarConversa(current.workspace.id, conversa.id); await carregar() } finally { setBusy(false) }
  }
  async function reabrir() {
    if (!conversa || !current) return
    setBusy(true)
    try { await reabrirConversa(current.workspace.id, conversa.id); await carregar() } finally { setBusy(false) }
  }
  async function enviar() {
    if (!conversa || !texto.trim()) return
    setBusy(true); setErro(null)
    const r = await enviarWhatsApp({ threadId: conversa.id, texto })
    setBusy(false)
    if (!r.ok) { setErro(r.erro ?? 'falha no envio'); return }
    setTexto(''); await carregar()
  }
  async function enviarTemplate() {
    if (!conversa || !tplId) return
    const tpl = templates.find(t => t.id === tplId)
    if (!tpl) return
    setBusy(true); setErro(null)
    const r = await enviarWhatsApp({
      threadId: conversa.id,
      template: { nome: tpl.nome, idioma: tpl.idioma, variaveis: tplVars.slice(0, tpl.variaveis) },
    })
    setBusy(false)
    if (!r.ok) { setErro(r.erro ?? 'falha no envio'); return }
    setTplId(''); setTplVars([]); await carregar()
  }

  if (!id) return null

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--muted)' }}>
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    )
  }
  if (!conversa) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--muted)' }}>
        Conversa não encontrada.
      </div>
    )
  }

  // Janela de 24h só existe na Cloud API oficial (Meta) — no Baileys (WhatsApp
  // Web não-oficial) não há restrição de horário nem exigência de template.
  const janelaAplicavel = canal?.provedor === 'cloud_api'
  const aberta = janelaAplicavel ? janelaAberta(conversa) : true
  const minha = conversa.atendente_user_id === user?.id
  const semAtendente = !conversa.atendente_user_id
  const tplSel = templates.find(t => t.id === tplId)

  return (
    <>
      {/* cabeçalho */}
      <header style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--card)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link
            to="/atendimento"
            className="btn-secondary m360-so-mobile"
            style={{ minHeight: 34, width: 34, padding: 0, justifyContent: 'center', flexShrink: 0 }}
            aria-label="Voltar para a lista"
          >
            <ArrowLeft size={16} aria-hidden />
          </Link>
          <span className={`conv-avatar${conversa.nao_lidas > 0 ? ' conv-avatar--nova' : ''}`} aria-hidden>
            {nomeConversa(conversa).slice(0, 1).toUpperCase()}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15.5 }}>
              {conversa.paciente_id
                ? <Link to={`/pacientes/${conversa.paciente_id}`} style={{ color: 'var(--text)' }}>{nomeConversa(conversa)}</Link>
                : nomeConversa(conversa)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {conversa.wa_telefone && <span>{formatarTelefone(conversa.wa_telefone)}</span>}
              {conversa.atendente_user_id ? (
                <span className="badge badge-ok" style={{ fontSize: 10 }}>
                  {minha ? 'Você' : nomeMembro(conversa.atendente_user_id)}
                </span>
              ) : (
                <span className="badge badge-warn" style={{ fontSize: 10 }}>Aguardando atendimento</span>
              )}
            </div>
          </div>
          {janelaAplicavel && (
            <span className={`badge ${aberta ? 'badge-ok' : 'badge-warn'}`} style={{ flexShrink: 0 }}>
              <Clock size={11} aria-hidden />{aberta ? `${horasRestantes(conversa)}h` : 'janela fechada'}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          {semAtendente && (
            <button className="btn-primary" style={{ minHeight: 32, padding: '4px 12px', fontSize: 12.5 }} disabled={busy} onClick={assumir}>
              Assumir atendimento
            </button>
          )}
          {minha && (
            <>
              <button className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }} disabled={busy} onClick={liberar}>Liberar</button>
              <button
                className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }} disabled={busy}
                onClick={() => setRepassando(v => !v)}
              >
                <Repeat size={13} aria-hidden /> Repassar atendimento
              </button>
            </>
          )}
          {!semAtendente && !minha && (
            <button className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }} disabled={busy} onClick={assumir}>Assumir mesmo assim</button>
          )}
          {!conversa.paciente_id && (
            <button className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }} onClick={() => setVinculando(v => !v)}>
              <UserPlus size={13} aria-hidden /> Vincular paciente
            </button>
          )}
          {conversa.paciente_id && (
            <button
              className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }} disabled={busy}
              onClick={async () => {
                if (!current || !user) return
                setBusy(true)
                try {
                  await converterEmTarefa({
                    workspaceId: current.workspace.id,
                    thread: {
                      ...conversa, paciente_id: conversa.paciente_id!, paciente_nome: nomeConversa(conversa),
                      assunto: null, consulta_id: null, prazo: null, tarefa_id: null, criado_por: null,
                      responsavel_user_id: conversa.atendente_user_id,
                    },
                    criadorUserId: user.id,
                  })
                  window.alert('Pendência criada a partir da conversa.')
                } finally { setBusy(false) }
              }}
            >
              Converter em pendência
            </button>
          )}
          {conversa.status !== 'encerrada' ? (
            <button className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }} disabled={busy} onClick={finalizar}>
              <CheckCircle2 size={13} aria-hidden /> Finalizar conversa
            </button>
          ) : (
            <button className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }} disabled={busy} onClick={reabrir}>
              <RotateCcw size={13} aria-hidden /> Reabrir conversa
            </button>
          )}
        </div>

        {repassando && minha && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              className="field-input" style={{ minHeight: 36, fontSize: 13, flex: 1, minWidth: 200 }}
              value={repasseAlvo} onChange={e => setRepasseAlvo(e.target.value)}
              aria-label="Repassar atendimento para"
            >
              <option value="">Repassar para…</option>
              {membros.filter(m => m.user_id && m.user_id !== user?.id).map(m => (
                <option key={m.user_id} value={m.user_id ?? ''}>{m.perfil?.nome ?? 'Equipe'} · {PAPEL_LABEL[m.papel]}</option>
              ))}
            </select>
            <button className="btn-primary" style={{ minHeight: 36, padding: '4px 12px', fontSize: 12.5 }} disabled={busy || !repasseAlvo} onClick={repassar}>
              Confirmar
            </button>
            <button className="btn-secondary" style={{ minHeight: 36, padding: '4px 10px', fontSize: 12.5 }} onClick={() => { setRepassando(false); setRepasseAlvo('') }}>
              Cancelar
            </button>
          </div>
        )}

        {vinculando && (
          <div style={{ position: 'relative', marginTop: 10 }}>
            <Search size={15} aria-hidden style={{ position: 'absolute', left: 12, top: 13, color: 'var(--muted)' }} />
            <input className="field-input" style={{ paddingLeft: 34, minHeight: 40 }} placeholder="Buscar paciente para vincular" value={buscaPac} onChange={e => setBuscaPac(e.target.value)} autoComplete="off" aria-label="Buscar paciente" />
            {sugestoes.length > 0 && (
              <div className="card" style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, top: 46, boxShadow: 'var(--shadow-float)', overflow: 'hidden' }}>
                {sugestoes.map(s => (
                  <button
                    key={s.id} type="button"
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 14px', background: 'none', border: 'none', fontSize: 14, borderBottom: '1px solid var(--border)' }}
                    onClick={async () => {
                      if (!current) return
                      await vincularPaciente(current.workspace.id, conversa.id, s.id)
                      setVinculando(false); setBuscaPac(''); setSugestoes([])
                      await carregar()
                    }}
                  >{s.nome}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </header>

      {/* mensagens */}
      <div className="atend-conversa-corpo">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 760, margin: '0 auto' }}>
          {mensagens.map(m => {
            const entrada = m.direcao === 'entrada'
            return (
              <div
                key={m.id}
                style={{
                  padding: '10px 13px', maxWidth: '78%', borderRadius: 12,
                  alignSelf: entrada ? 'flex-start' : 'flex-end',
                  background: m.rascunho ? 'var(--warn-soft)' : entrada ? 'var(--card)' : 'var(--primary-soft)',
                  border: m.rascunho ? '1px dashed var(--warn)' : '1px solid var(--border)',
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                <div style={{ fontSize: 14.5, whiteSpace: 'pre-wrap' }}>{m.conteudo}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, display: 'flex', gap: 5, alignItems: 'center', justifyContent: entrada ? 'flex-start' : 'flex-end' }}>
                  {!entrada && <span>{nomeMembro(m.autor_user_id)}</span>}
                  <span>{new Date(m.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                  {m.template_nome && <span>· template</span>}
                  {!entrada && m.status_entrega === 'enviada' && <Check size={12} aria-label="enviada" />}
                  {!entrada && m.status_entrega === 'entregue' && <CheckCheck size={12} aria-label="entregue" />}
                  {!entrada && m.status_entrega === 'lida' && <CheckCheck size={12} aria-label="lida" style={{ color: 'var(--primary)' }} />}
                  {!entrada && m.status_entrega === 'falhou' && <AlertTriangle size={12} aria-label="falhou" style={{ color: 'var(--danger)' }} />}
                </div>
              </div>
            )
          })}
          <div ref={fimRef} />
        </div>
      </div>

      {/* composer */}
      <div style={{ borderTop: '1px solid var(--border)', background: 'var(--card)', padding: 12, flexShrink: 0 }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          {erro && <div className="field-error" role="alert" style={{ marginBottom: 8 }}>{erro}</div>}

          {aberta ? (
            <>
              {modelos.length > 0 && (
                <select
                  className="field-input" style={{ minHeight: 34, fontSize: 12.5, marginBottom: 8 }}
                  value="" onChange={e => {
                    const m = modelos.find(x => x.id === e.target.value)
                    if (m) setTexto(prev => (prev ? prev + '\n' : '') + m.conteudo)
                  }}
                  aria-label="Inserir modelo de resposta"
                >
                  <option value="">Inserir modelo de resposta…</option>
                  {modelos.map(m => <option key={m.id} value={m.id}>{m.titulo}</option>)}
                </select>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <textarea
                  className="field-input" rows={2} style={{ resize: 'none', flex: 1 }}
                  placeholder={semAtendente ? 'Assuma o atendimento para responder…' : 'Escrever mensagem…'}
                  value={texto} onChange={e => setTexto(e.target.value)}
                  disabled={semAtendente} aria-label="Mensagem"
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar() } }}
                />
                <button className="btn-primary" style={{ minHeight: 44, width: 48, padding: 0, justifyContent: 'center', flexShrink: 0 }} disabled={busy || semAtendente || !texto.trim()} onClick={enviar} aria-label="Enviar">
                  {busy ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden /> : <Send size={16} aria-hidden />}
                </button>
              </div>
              <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
            </>
          ) : (
            <div>
              <div className="badge badge-warn" style={{ marginBottom: 8, display: 'block', padding: '8px 12px', fontSize: 12.5 }}>
                <AlertTriangle size={12} aria-hidden /> Mais de 24h desde a última mensagem da paciente — só um <strong>template aprovado</strong> reabre a conversa.
              </div>
              {templates.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                  Nenhum template aprovado. <Link to="/configuracoes/whatsapp" style={{ color: 'var(--primary-deep)', fontWeight: 600 }}>Cadastrar template</Link>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <select className="field-input" style={{ minHeight: 38, fontSize: 13.5 }} value={tplId} onChange={e => { setTplId(e.target.value); setTplVars([]) }} aria-label="Template">
                    <option value="">Escolher template aprovado…</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                  </select>
                  {tplSel && Array.from({ length: tplSel.variaveis }).map((_, i) => (
                    <input
                      key={i} className="field-input" style={{ minHeight: 36, fontSize: 13.5 }}
                      placeholder={`Variável {{${i + 1}}}`} value={tplVars[i] ?? ''}
                      onChange={e => setTplVars(prev => { const c = [...prev]; c[i] = e.target.value; return c })}
                      aria-label={`Variável ${i + 1}`}
                    />
                  ))}
                  {tplSel && (
                    <div style={{ fontSize: 13, background: 'var(--bg)', padding: '9px 12px', borderRadius: 8, color: 'var(--text-secondary)' }}>
                      {preencherTemplate(tplSel.corpo, tplVars)}
                    </div>
                  )}
                  <button className="btn-primary" style={{ minHeight: 40, alignSelf: 'flex-start' }} disabled={busy || !tplId || semAtendente} onClick={enviarTemplate}>
                    <Send size={15} aria-hidden /> Enviar template
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
